import React, { useState, useEffect, useRef, useCallback } from 'react';
import { isOwnerRole, isManagerRole, isStaffRole } from '@rsmaids/shared/constants/roles';
import { useScrollSnap } from '../hooks/useScrollSnap';
import {
  IonContent,
  IonPage,
  IonList,
  IonItem,
  IonIcon,
  IonAlert,
  useIonRouter,
  IonCard,
  IonCardContent,
  IonSegment,
  IonSegmentButton,
  IonLabel,
  IonButton,
  IonGrid,
  IonModal
} from '@ionic/react';
import { useRemountOnPageHidden } from '../hooks/useRemountOnPageHidden';
import {
  personOutline,
  helpCircleOutline,
  notificationsOutline,
  lockClosedOutline,
  logOutOutline,
  sunnyOutline,
  moonOutline,
  contrastOutline,
  businessOutline,
  peopleOutline,
  gridOutline,
  cloudUploadOutline,
  cardOutline,
  chatbubblesOutline,
  shieldCheckmarkOutline,
  desktopOutline,
  searchOutline,
  callOutline,
  chevronForwardOutline
} from 'ionicons/icons';
import { useAuth } from '../contexts';
import { DataServiceFactory } from '@rsmaids/shared/services/DataServiceFactory';
import type { ServiceInfo, ServiceType } from '@rsmaids/shared/types/services';
import { Security } from './Security/Security';
import EmployeeManagement from './EmployeeManagement';
import IvrAgents from './IvrAgents';
import ProfilePage from './dashboard/Profile';
import NotificationSettings from './NotificationSettings';
import BusinessProfile from './BusinessProfile';
import ServiceTypeModal from '../components/ServiceTypeModal';
import Booking from './bookingSystem/BookingForm';
import styles from './ManagePage.module.scss';
import BasePageWithHeader, { TitleCard } from '../components/BasePageWithHeader';
import SearchModal from '../components/SearchModal';
import LanguageSetting from '../components/LanguageSetting';
import { useTranslation } from 'react-i18next';



const ManagePage: React.FC = () => {
  const { t } = useTranslation();
  const router = useIonRouter();
  const { authState, actions } = useAuth();
  const { logout } = actions;
  const { user } = authState;

  const [showLogoutAlert, setShowLogoutAlert] = useState(false);
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [showBusinessProfileModal, setShowBusinessProfileModal] = useState(false);
  const [showSecurityModal, setShowSecurityModal] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showEmployeeModal, setShowEmployeeModal] = useState(false);
  const [showIvrAgentsModal, setShowIvrAgentsModal] = useState(false);
  const [showNotificationsModal, setShowNotificationsModal] = useState(false);
  const [showServicesModal, setShowServicesModal] = useState(false);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [serviceTypesLoading, setServiceTypesLoading] = useState(false);
  const [serviceTypeOptions, setServiceTypeOptions] = useState<ServiceInfo[]>([]);
  const [selectedServiceType, setSelectedServiceType] = useState<ServiceType | ''>('');
  const [selectedServiceTypeForBooking, setSelectedServiceTypeForBooking] = useState<ServiceType | undefined>(undefined);

  const ionContentRef = useRef<HTMLIonContentElement | null>(null);
  const titleCardRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLIonGridElement>(null);
  const rafTick = useRef(false);
  const collapseDistance = 66;

  // Handle scroll for header and title card animation
  const applyProgress = useCallback((progress: number, smooth: boolean) => {
    const page = ionContentRef.current?.closest('.ion-page');
    const headerEl = page?.querySelector<HTMLElement>('[data-header-title]');

    if (smooth) {
      if (headerEl) headerEl.style.transition = 'opacity 0.2s ease-out';
      if (titleCardRef.current) {
        titleCardRef.current.style.transition = 'opacity 0.2s ease-out, transform 0.2s ease-out';
      }
      setTimeout(() => {
        if (headerEl) headerEl.style.transition = '';
        if (titleCardRef.current) titleCardRef.current.style.transition = '';
      }, 220);
    }

    headerEl?.style.setProperty('--title-opacity', String(progress));

    if (titleCardRef.current) {
      titleCardRef.current.style.setProperty('--content-opacity', String(1 - progress));
      titleCardRef.current.style.setProperty('--content-scale', `scale(${1 - (progress * 0.2)})`);
    }
  }, []);

  const handleScroll = useScrollSnap(contentRef, collapseDistance, applyProgress);

  // The list is rebuilt while the page is hidden, so it is back at the top on return
  const scrollResetKey = useRemountOnPageHidden(contentRef);
  // The header's collapsed/expanded look is driven by scroll events, and a rebuilt list at
  // scroll 0 fires none — so put the header back to expanded (title card shown, small title
  // hidden) explicitly, otherwise both show at once.
  useEffect(() => {
    applyProgress(0, false);
  }, [scrollResetKey, applyProgress]);

  // Theme state and functions
  const [currentTheme, setCurrentTheme] = useState(() => {
    return localStorage.getItem('theme') || 'system';
  });

  const applyTheme = (theme: string) => {
    localStorage.setItem('theme', theme);
    setCurrentTheme(theme);

    // Use the global theme function
    if (window.applyTheme) {
      window.applyTheme(theme);
    }
  };

  // Initialize theme on mount — currentTheme already reads this value via
  // its lazy useState initializer above, so only the external-system call
  // (window.applyTheme) needs to happen here. Re-reads localStorage directly
  // (rather than closing over currentTheme) so this stays a true mount-only
  // effect.
  useEffect(() => {
    if (window.applyTheme) {
      window.applyTheme(localStorage.getItem('theme') || 'system');
    }
  }, []);

  useEffect(() => {
    let isMounted = true;
    if (!showServicesModal || serviceTypeOptions.length > 0) return () => { isMounted = false; };

    const fetchServices = async () => {
      setServiceTypesLoading(true);
      try {
        const dataService = DataServiceFactory.getService();
        const servicesData = await dataService.getServiceTypes();
        if (isMounted) setServiceTypeOptions(servicesData);
      } catch (error) {
        console.error('Failed to load service types for Services modal:', error);
      } finally {
        if (isMounted) setServiceTypesLoading(false);
      }
    };

    fetchServices();
    return () => { isMounted = false; };
  }, [showServicesModal, serviceTypeOptions.length]);

  const navigateTo = (path: string) => {
    router.push(path);
  };

  const handleLogout = async () => {
    try {
      await logout(); // This will clear auth data from context

      // Redirect to login page
      router.push('/login', 'root', 'replace');
    } catch {
      // Silent error handling
    }
  };

  return (
    <IonPage className={styles.page}>
      <BasePageWithHeader
        title={t('pages.Manage.manageYourBusiness')}
        toolbarClassName={styles.toolbar}
        smallTitleClassName={styles.smallTitle}
        titleHeaderClassName={styles.titleHeader}
        startButtons={
          <IonButton shape='round' className={styles.searchButton} style={{ visibility: 'hidden' }}>
            <IonIcon icon={searchOutline} />
          </IonButton>
        }
        endButtons={
          <IonButton shape='round' className={styles.searchButton} onClick={() => setShowSearchModal(true)}>
            <IonIcon icon={searchOutline} />
          </IonButton>
        }
      >
        <IonContent ref={ionContentRef} className={styles.content}>
          <IonGrid
            key={scrollResetKey}
            ref={contentRef}
            className={styles.manageContainer}
            onScroll={(e) => {
              const top = (e.target as HTMLElement).scrollTop || 0;
              if (!rafTick.current) {
                rafTick.current = true;
                requestAnimationFrame(() => {
                  handleScroll(top);
                  rafTick.current = false;
                });
              }
            }}
          >
            <div ref={titleCardRef} className={styles.titleCardWrapper}>
              <TitleCard
                title={t('pages.Manage.rSMaidsService')}
                subtitle={t('pages.Manage.manageYourBusiness')}
                subtitleClassName={styles.heroSubtitle}
                cardClassName={styles.manageOverviewCard}
                titleContainerClassName={styles.titleContainer}
                titleClassName={styles.manageTitle}
                titleAvatarClassName={styles.companyLogo}
                avatarUrl="/maskable_icon_x512.png"
              />
            </div>

            {/* Business Tools */}
            <IonCard className={styles.sectionCard}>
              <IonCardContent className={styles.section}>
                <IonLabel className={styles.sectionTitle}>{t('pages.Manage.businessTools')}</IonLabel>
                <IonList lines="none" className={styles.settingsList}>
                  <IonItem button detail detailIcon={chevronForwardOutline} lines="none" onClick={() => setShowBusinessProfileModal(true)} className={styles.settingsItem}>
                    <div slot="start" className={`${styles.iconBadge} ${styles.iconBadgePrimary}`}>
                      <IonIcon icon={businessOutline} />
                    </div>
                    <IonLabel>
                      <h3>{t('pages.Manage.businessProfile')}</h3>
                      <p>{t('pages.Manage.manageYourBusinessInformation')}</p>
                    </IonLabel>
                  </IonItem>
                  <IonItem button detail detailIcon={chevronForwardOutline} lines="none" onClick={() => navigateTo('/tabs/billing')} className={styles.settingsItem}>
                    <div slot="start" className={`${styles.iconBadge} ${styles.iconBadgeSecondary}`}>
                      <IonIcon icon={cardOutline} />
                    </div>
                    <IonLabel>
                      <h3>{t('pages.Manage.billingSubscription')}</h3>
                      <p>{t('pages.Manage.manageBillingAndPaymentDetails')}</p>
                    </IonLabel>
                  </IonItem>
                  <IonItem button detail detailIcon={chevronForwardOutline} lines="none" onClick={() => navigateTo('/team/members')} className={styles.settingsItem}>
                    <div slot="start" className={`${styles.iconBadge} ${styles.iconBadgeTertiary}`}>
                      <IonIcon icon={peopleOutline} />
                    </div>
                    <IonLabel>
                      <h3>{t('pages.Manage.teamMembers')}</h3>
                      <p>{t('pages.Manage.viewAndManageTeamMembers')}</p>
                    </IonLabel>
                  </IonItem>
                  {(isOwnerRole(user?.role) || isManagerRole(user?.role)) && (
                    <IonItem button detail detailIcon={chevronForwardOutline} lines="none" onClick={() => setShowEmployeeModal(true)} className={styles.settingsItem}>
                      <div slot="start" className={`${styles.iconBadge} ${styles.iconBadgePrimary}`}>
                        <IonIcon icon={shieldCheckmarkOutline} />
                      </div>
                      <IonLabel>
                        <h3>{t('pages.Manage.employeeManagement')}</h3>
                        <p>{t('pages.Manage.manageEmployeePermissionsAndRoles')}</p>
                      </IonLabel>
                    </IonItem>
                  )}
                  {(isOwnerRole(user?.role) || isManagerRole(user?.role) || isStaffRole(user?.role)) && (
                    <IonItem button detail detailIcon={chevronForwardOutline} lines="none" onClick={() => setShowIvrAgentsModal(true)} className={styles.settingsItem}>
                      <div slot="start" className={`${styles.iconBadge} ${styles.iconBadgeSecondary}`}>
                        <IonIcon icon={callOutline} />
                      </div>
                      <IonLabel>
                        <h3>{t('pages.Manage.phoneAgents')}</h3>
                        <p>{t('pages.Manage.manageWhoAnswersIncomingCalls')}</p>
                      </IonLabel>
                    </IonItem>
                  )}
                  <IonItem button detail detailIcon={chevronForwardOutline} lines="none" onClick={() => setShowServicesModal(true)} className={styles.settingsItem}>
                    <div slot="start" className={`${styles.iconBadge} ${styles.iconBadgeTertiary}`}>
                      <IonIcon icon={gridOutline} />
                    </div>
                    <IonLabel>
                      <h3>{t('pages.Manage.bookingServices')}</h3>
                      <p>{t('pages.Manage.configureAvailableServices')}</p>
                    </IonLabel>
                  </IonItem>
                </IonList>
              </IonCardContent>
            </IonCard>

            {/* Account Settings */}
            <IonCard className={styles.sectionCard}>
              <IonCardContent className={styles.section}>
                <IonLabel className={styles.sectionTitle}>{t('pages.Manage.accountSettings')}</IonLabel>
                <IonList lines="none" className={styles.settingsList}>
                  <IonItem button detail detailIcon={chevronForwardOutline} lines="none" onClick={() => setShowProfileModal(true)} className={styles.settingsItem}>
                    <div slot="start" className={`${styles.iconBadge} ${styles.iconBadgePrimary}`}>
                      <IonIcon icon={personOutline} />
                    </div>
                    <IonLabel>
                      <h3>{t('pages.Manage.myProfile')}</h3>
                      <p>{t('pages.Manage.viewAndEditYourProfileInformation')}</p>
                    </IonLabel>
                  </IonItem>
                  <IonItem button detail detailIcon={chevronForwardOutline} lines="none" onClick={() => setShowNotificationsModal(true)} className={styles.settingsItem}>
                    <div slot="start" className={`${styles.iconBadge} ${styles.iconBadgeSecondary}`}>
                      <IonIcon icon={notificationsOutline} />
                    </div>
                    <IonLabel>
                      <h3>{t('pages.Manage.notifications')}</h3>
                      <p>{t('pages.Manage.manageNotificationPreferences')}</p>
                    </IonLabel>
                  </IonItem>
                  <LanguageSetting
                    itemClassName={styles.settingsItem}
                    badgeClassName={`${styles.iconBadge} ${styles.iconBadgeSecondary}`}
                  />
                  <IonItem button detail detailIcon={chevronForwardOutline} lines="none" onClick={() => setShowSecurityModal(true)} className={styles.settingsItem}>
                    <div slot="start" className={`${styles.iconBadge} ${styles.iconBadgeWarning}`}>
                      <IonIcon icon={lockClosedOutline} />
                    </div>
                    <IonLabel>
                      <h3>{t('pages.Manage.security')}</h3>
                      <p>{t('pages.Manage.passwordAndAuthenticationSettings')}</p>
                    </IonLabel>
                  </IonItem>
                  <IonItem button detail detailIcon={chevronForwardOutline} lines="none" onClick={() => navigateTo('/integrations')} className={styles.settingsItem}>
                    <div slot="start" className={`${styles.iconBadge} ${styles.iconBadgeTertiary}`}>
                      <IonIcon icon={cloudUploadOutline} />
                    </div>
                    <IonLabel>
                      <h3>{t('pages.Manage.integrations')}</h3>
                      <p>{t('pages.Manage.connectThirdPartyServices')}</p>
                    </IonLabel>
                  </IonItem>
                </IonList>
              </IonCardContent>
            </IonCard>

            {/* Theme Settings */}
            <IonCard className={styles.sectionCard}>
              <IonCardContent className={styles.section}>
                <IonLabel className={styles.sectionTitle}>{t('pages.Manage.theme')}</IonLabel>
                <IonList lines="none" className={styles.settingsList}>
                  <IonItem lines="none" className={`${styles.settingsItem} ${styles.themeItem}`}>
                    <div className={styles.themeWrapper}>
                      <div className={styles.themeHeader}>
                          <div className={`${styles.iconBadge} ${styles.iconBadgePrimary}`}>
                            <IonIcon icon={contrastOutline} />
                          </div>
                        <IonLabel>
                          <h3>{t('pages.Manage.appearance')}</h3>
                          <p>{t('pages.Manage.chooseYourPreferredTheme')}</p>
                        </IonLabel>
                      </div>
                      <IonSegment
                        value={currentTheme}
                        onIonChange={(e) => {
                          console.log('Theme selection changed to:', e.detail.value);
                          console.log('Current document classes before change:', document.documentElement.classList.toString());
                          applyTheme(e.detail.value as string);
                          // Add a small delay to check after the change
                          setTimeout(() => {
                            console.log('Current document classes after change:', document.documentElement.classList.toString());
                          }, 100);
                        }}
                        className={styles.themeSegment}
                      >
                        <IonSegmentButton value="light">
                          <IonIcon icon={sunnyOutline} />
                        </IonSegmentButton>
                        <IonSegmentButton value="dark">
                          <IonIcon icon={moonOutline} />
                        </IonSegmentButton>
                        <IonSegmentButton value="system">
                          <IonIcon icon={desktopOutline} />
                        </IonSegmentButton>
                      </IonSegment>
                    </div>
                  </IonItem>
                </IonList>
              </IonCardContent>
            </IonCard>

            {/* Support */}
            <IonCard className={styles.sectionCard}>
              <IonCardContent className={styles.section}>
                <IonLabel className={styles.sectionTitle}>{t('pages.Manage.support')}</IonLabel>
                <IonList lines="none" className={styles.settingsList}>
                  <IonItem button detail detailIcon={chevronForwardOutline} lines="none" onClick={() => navigateTo('/help')} className={styles.settingsItem}>
                    <div slot="start" className={`${styles.iconBadge} ${styles.iconBadgePrimary}`}>
                      <IonIcon icon={helpCircleOutline} />
                    </div>
                    <IonLabel>
                      <h3>{t('pages.Manage.helpCenter')}</h3>
                      <p>{t('pages.Manage.browseFaqsAndGuides')}</p>
                    </IonLabel>
                  </IonItem>
                  <IonItem button detail detailIcon={chevronForwardOutline} lines="none" onClick={() => navigateTo('/contact-support')} className={styles.settingsItem}>
                    <div slot="start" className={`${styles.iconBadge} ${styles.iconBadgeSecondary}`}>
                      <IonIcon icon={chatbubblesOutline} />
                    </div>
                    <IonLabel>
                      <h3>{t('pages.Manage.contactSupport')}</h3>
                      <p>{t('pages.Manage.getHelpFromOurSupportTeam')}</p>
                    </IonLabel>
                  </IonItem>
                </IonList>
              </IonCardContent>
            </IonCard>

            {/* Logout */}
            <IonCard className={styles.sectionCard}>
              <IonCardContent className={styles.section}>
                <IonList lines="none" className={styles.settingsList}>
                  <IonItem button detail={false} lines="none" onClick={() => setShowLogoutAlert(true)} className={`${styles.settingsItem} ${styles.logoutItem}`}>
                    <div slot="start" className={`${styles.iconBadge} ${styles.iconBadgeDanger}`}>
                      <IonIcon icon={logOutOutline} />
                    </div>
                    <IonLabel>
                      <h3>{t('pages.Manage.logOut')}</h3>
                      <p>{t('pages.Manage.signOutOfYourAccount')}</p>
                    </IonLabel>
                  </IonItem>
                </IonList>
              </IonCardContent>
            </IonCard>

            {/* Logout Confirmation Alert */}
            <IonAlert
              isOpen={showLogoutAlert}
              onDidDismiss={() => setShowLogoutAlert(false)}
              header={t('pages.Manage.logOut')}
              message={t('pages.Manage.areYouSureYouWantTo')}
              cssClass="logout-alert"
              buttons={[
                {
                  text: t('pages.Manage.cancel'),
                  role: 'cancel',
                  handler: () => {
                    setShowLogoutAlert(false);
                  },
                },
                {
                  text: t('pages.Manage.logOut'),
                  handler: () => {
                    handleLogout();
                  },
                },
              ]}
            />

            <SearchModal
              isOpen={showSearchModal}
              onClose={() => setShowSearchModal(false)}
            />
            <IonModal
              isOpen={showBusinessProfileModal}
              onDidDismiss={() => setShowBusinessProfileModal(false)}
              canDismiss={true}
              backdropDismiss={true}
              keepContentsMounted={false}
              handle={false}
            >
              <BusinessProfile onClose={() => setShowBusinessProfileModal(false)} />
            </IonModal>
            <IonModal
              isOpen={showSecurityModal}
              onDidDismiss={() => setShowSecurityModal(false)}
              canDismiss={true}
              backdropDismiss={true}
              keepContentsMounted={false}
              handle={false}
            >
              <Security onClose={() => setShowSecurityModal(false)} />
            </IonModal>
            <IonModal
              isOpen={showProfileModal}
              onDidDismiss={() => setShowProfileModal(false)}
              canDismiss={true}
              backdropDismiss={true}
              keepContentsMounted={false}
              handle={false}
            >
              <ProfilePage onClose={() => setShowProfileModal(false)} />
            </IonModal>
            <IonModal
              isOpen={showEmployeeModal}
              onDidDismiss={() => setShowEmployeeModal(false)}
              canDismiss={true}
              backdropDismiss={true}
              keepContentsMounted={false}
              handle={false}
            >
              <EmployeeManagement
                onClose={() => setShowEmployeeModal(false)}
                onOpenPhoneAgents={() => {
                  setShowEmployeeModal(false);
                  setTimeout(() => setShowIvrAgentsModal(true), 300);
                }}
              />
            </IonModal>
            <IonModal
              isOpen={showIvrAgentsModal}
              onDidDismiss={() => setShowIvrAgentsModal(false)}
              canDismiss={true}
              backdropDismiss={true}
              keepContentsMounted={false}
              handle={false}
              className={styles.ivrAgentsModal}
            >
              <IvrAgents onClose={() => setShowIvrAgentsModal(false)} />
            </IonModal>
            <IonModal
              isOpen={showNotificationsModal}
              onDidDismiss={() => setShowNotificationsModal(false)}
              canDismiss={true}
              backdropDismiss={true}
              keepContentsMounted={false}
              handle={false}
            >
              <NotificationSettings onClose={() => setShowNotificationsModal(false)} />
            </IonModal>

            <Booking
              isOpen={showBookingModal}
              onClose={() => {
                setShowBookingModal(false);
                setSelectedServiceTypeForBooking(undefined);
              }}
              initialServiceType={selectedServiceTypeForBooking}
            />

            <ServiceTypeModal
              isOpen={showServicesModal}
              onClose={() => setShowServicesModal(false)}
              onSelectServiceType={(serviceType) => {
                setSelectedServiceType(serviceType);
                setSelectedServiceTypeForBooking(serviceType);
                setShowServicesModal(false);
                setShowBookingModal(true);
              }}
              selectedServiceType={selectedServiceType}
              services={serviceTypeOptions}
              loading={serviceTypesLoading}
            />
          </IonGrid>
        </IonContent>
      </BasePageWithHeader>
    </IonPage>
  );
};

export default ManagePage;